import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Film, Plus, Trash2, Upload } from 'lucide-react';
import * as installationApi from '@/api/installation.api';
import type { MessagesConfig, PropositoDeVideo } from '@/api/installation.api';
import { subirVideo } from '@/api/proyectos.api';
import { ApiError } from '@/api/client';
import { PageHeader } from '@/components/common/PageHeader';
import { MensajesTab } from '@/components/installation/MensajesTab';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { MAX_ENTRADAS, REDES, VIDEOS_DE_PRESENTACION, ajustesDesdeConfig, configConAjustes, erroresDeAjustes, type AjustesForm } from '@/lib/ajustes-del-agente';
import { AgendaDelZoomView } from '@/components/agenda/AgendaDelZoomView';
import { agendaDesdeConfig, conAgenda, erroresDeAgenda, type AgendaForm } from '@/lib/agenda-zoom';

/** Las plantillas que el dueño edita aquí: su aviso caliente. Los demás avisos son interruptores. */
const PLANTILLAS_DEL_DUENO: Array<keyof MessagesConfig> = ['hotLeadOwner'];

/**
 * AJUSTES DEL AGENTE (tanda 3, F19). Ver lib/ajustes-del-agente.ts. Debajo van
 * los avisos (interruptores y a quién le llega cada uno) con el mismo componente
 * de Instalación, filtrado a lo del dueño.
 */
export default function AjustesDelAgente() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: cfg, isLoading } = useQuery({ queryKey: ['installation', 'config'], queryFn: installationApi.getInstallationConfig });
  const [form, setForm] = useState<AjustesForm | null>(null);
  const [messages, setMessages] = useState<MessagesConfig | null>(null);
  const [agenda, setAgenda] = useState<AgendaForm | null>(null);
  // Con cambios sin guardar, un refetch (al volver a la ventana) NO pisa lo que
  // el dueño está escribiendo (revisión de Codex). Al guardar se vuelve a leer del servidor.
  const sinGuardar = useRef(false);

  useEffect(() => {
    if (cfg && !sinGuardar.current) {
      setForm(ajustesDesdeConfig(cfg));
      setMessages({ ...cfg.messages });
      setAgenda(agendaDesdeConfig(cfg));
    }
  }, [cfg]);

  const editarForm = (f: AjustesForm) => {
    sinGuardar.current = true;
    setForm(f);
  };

  const guardar = useMutation({
    mutationFn: () => installationApi.saveInstallationConfig(conAgenda(configConAjustes(cfg!, form!, messages!), agenda!)),
    onSuccess: () => {
      sinGuardar.current = false;
      void qc.invalidateQueries({ queryKey: ['installation'] });
      toast.success('Ajustes guardados');
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'No se pudieron guardar los ajustes'),
  });

  // Tanda 5: el video se sube ya; su clave se guarda con «Guardar cambios». Se escribe sobre el
  // formulario de ESE momento: lo que el dueño tecleó mientras subía no se pierde.
  const video = useMutation({
    mutationFn: ({ clave, archivo }: { clave: PropositoDeVideo; archivo: File }) => subirVideo(archivo).then((r) => ({ clave, key: r.key })),
    onSuccess: ({ clave, key }) => {
      sinGuardar.current = true;
      setForm((f) => (f ? { ...f, videos: { ...f.videos, [clave]: { ...f.videos[clave], key } } } : f));
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'No se pudo subir el video'),
  });

  if (isLoading || !cfg || !form || !messages || !agenda) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }
  const errores = erroresDeAjustes(form);
  const erroresAgenda = erroresDeAgenda(agenda);
  return (
    <div className="max-w-4xl space-y-6">
      <PageHeader
        title="Ajustes del agente"
        description="Lo que el agente usa para calificar y ofrecer el Zoom, y los avisos que te llegan."
        action={
          <Button
            onClick={() => guardar.mutate()}
            loading={guardar.isPending}
            disabled={video.isPending || Object.keys(errores).length > 0 || Object.keys(erroresAgenda).length > 0}
          >
            Guardar cambios
          </Button>
        }
      />
      <AjustesView
        form={form}
        errores={errores}
        conGuion={!!cfg.crm.guion}
        conAgenda={agenda.activa}
        onChange={editarForm}
        subiendoVideo={video.isPending ? video.variables.clave : null}
        onSubirVideo={(clave, archivo) => video.mutate({ clave, archivo })}
      />
      {cfg.crm.guion && (
        <AgendaDelZoomView
          form={agenda}
          errores={erroresAgenda}
          onChange={(f) => {
            // Igual que los demás campos: un refetch no pisa lo que el dueño está escribiendo.
            sinGuardar.current = true;
            setAgenda(f);
          }}
        />
      )}
      <MensajesTab
        messages={messages}
        onChange={(p) => {
          sinGuardar.current = true;
          setMessages((m) => (m ? { ...m, ...p } : m));
        }}
        claves={PLANTILLAS_DEL_DUENO}
      />
    </div>
  );
}

const titulo = 'text-sm font-semibold text-slate-900 dark:text-slate-100';
const ayuda = 'mt-0.5 text-xs text-slate-500 dark:text-slate-400';

/** El formulario, sin estado: lo prueba renderToStaticMarkup. */
export function AjustesView({
  form,
  errores,
  conGuion,
  conAgenda = false,
  onChange,
  subiendoVideo = null,
  onSubirVideo,
}: {
  form: AjustesForm;
  errores: Record<string, string>;
  conGuion: boolean;
  /** Ola C: con la agenda del Zoom encendida, las franjas de texto ya no se usan. */
  conAgenda?: boolean;
  onChange: (f: AjustesForm) => void;
  /** Tanda 5: el video que se está subiendo (uno a la vez). */
  subiendoVideo?: PropositoDeVideo | null;
  onSubirVideo?: (clave: PropositoDeVideo, archivo: File) => void;
}) {
  const set = (patch: Partial<AjustesForm>) => onChange({ ...form, ...patch });
  const setVideo = (clave: PropositoDeVideo, patch: Partial<AjustesForm['videos'][PropositoDeVideo]>) =>
    set({ videos: { ...form.videos, [clave]: { ...form.videos[clave], ...patch } } });
  return (
    <div className="space-y-4">
      <Card>
        <CardBody className="space-y-3 p-4">
          <div>
            <h2 className={titulo}>Mínimo por zona (US$)</h2>
            <p className={ayuda}>
              Si el presupuesto del cliente llega al mínimo de su zona, «cuadra». Vacío = cualquier presupuesto cuadra. Los
              que trae la demo son de ejemplo: cámbialos por los tuyos.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {Object.entries(form.minimos).map(([zona, v]) => (
              <Input
                key={zona}
                label={zona}
                inputMode="numeric"
                placeholder="sin mínimo"
                value={v}
                error={errores[`minimo:${zona}`]}
                onChange={(e) => set({ minimos: { ...form.minimos, [zona]: e.target.value } })}
              />
            ))}
          </div>
        </CardBody>
      </Card>

      {conGuion && (
        <>
          {!conAgenda && (
            <Card>
              <CardBody className="space-y-3 p-4">
                <div>
                  <h2 className={titulo}>Franjas del Zoom</h2>
                  <p className={ayuda}>
                    El agente ofrece las dos primeras, tal como las escribes («mañana a las 4:00 pm»), en hora de Miami. Con
                    menos de dos, le dice al cliente que tú le escribes para coordinar.
                  </p>
                </div>
                {form.franjas.map((f, i) => (
                  <div key={i} className="flex items-end gap-2">
                    <Input
                      aria-label={`Franja ${i + 1}`}
                      value={f}
                      onChange={(e) => set({ franjas: form.franjas.map((x, j) => (j === i ? e.target.value : x)) })}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Quitar la franja ${i + 1}`}
                      onClick={() => set({ franjas: form.franjas.filter((_, j) => j !== i) })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => set({ franjas: [...form.franjas, ''] })}>
                  <Plus className="h-4 w-4" /> Agregar franja
                </Button>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardBody className="space-y-3 p-4">
              <div>
                <h2 className={titulo}>Cómo le habla al cliente</h2>
                <p className={ayuda}>Todas las preguntas y respuestas del agente siguen este trato.</p>
              </div>
              <Select
                label="Trato"
                className="max-w-xs"
                value={form.trato}
                onChange={(e) => set({ trato: e.target.value === 'usted' ? 'usted' : 'tu' })}
                options={[
                  { value: 'usted', label: 'De usted' },
                  { value: 'tu', label: 'De tú' },
                ]}
              />
            </CardBody>
          </Card>

          <Card>
            <CardBody className="space-y-3 p-4">
              <div>
                <h2 className={titulo}>Tu web y tus redes</h2>
                <p className={ayuda}>
                  El agente invita a verlas al terminar las preguntas, solo con los enlaces que pongas aquí. Vacío = no invita.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {REDES.map((r) => (
                  <Input
                    key={r.clave}
                    label={r.etiqueta}
                    placeholder="https://…"
                    value={form.enlaces[r.clave]}
                    error={errores[`enlace:${r.clave}`]}
                    onChange={(e) => set({ enlaces: { ...form.enlaces, [r.clave]: e.target.value } })}
                  />
                ))}
              </div>
            </CardBody>
          </Card>

          {/*
            LAS ENTRADAS DEL EVENTO.
            Es lo único de esta pantalla que el agente le DICE a un cliente, así que es lo
            único que de verdad hay que poder cambiar sin llamar a nadie. Antes vivían en el
            código y Enrique no las podía tocar: el mismo caso del plan que subió de precio y
            siguió mostrando el viejo en una pantalla.
          */}
          <Card>
            <CardBody className="space-y-3 p-4">
              <div>
                <h2 className={titulo}>Entradas del evento</h2>
                <p className={ayuda}>
                  Lo que el agente contesta cuando preguntan cuánto cuesta. Solo dice estos precios: si borras la lista, vuelve a decir que lo
                  confirma una persona. En pesos, solo el número. Lo que incluye, separado por punto y coma.
                </p>
              </div>
              {form.entradas.map((e, i) => (
                <div key={i} className="space-y-2 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                  <div className="flex flex-wrap gap-2">
                    <div className="min-w-[8rem] grow">
                      <Input
                        id={`entrada-nivel-${i}`}
                        aria-label={`Nombre de la entrada ${i + 1}`}
                        placeholder="Gold"
                        value={e.nivel}
                        onChange={(ev) => onChange({ ...form, entradas: form.entradas.map((x, j) => (j === i ? { ...x, nivel: ev.target.value } : x)) })}
                      />
                      {errores[`entrada:${i}:nivel`] && <p className="mt-1 text-xs text-red-600">{errores[`entrada:${i}:nivel`]}</p>}
                    </div>
                    <div className="w-32">
                      <Input
                        id={`entrada-precio-${i}`}
                        aria-label={`Precio de la entrada ${i + 1}`}
                        placeholder="6600"
                        inputMode="numeric"
                        value={e.precio}
                        onChange={(ev) => onChange({ ...form, entradas: form.entradas.map((x, j) => (j === i ? { ...x, precio: ev.target.value } : x)) })}
                      />
                      {errores[`entrada:${i}:precio`] && <p className="mt-1 text-xs text-red-600">{errores[`entrada:${i}:precio`]}</p>}
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      aria-label={`Quitar la entrada ${i + 1}`}
                      onClick={() => onChange({ ...form, entradas: form.entradas.filter((_, j) => j !== i) })}
                    >
                      Quitar esta entrada
                    </Button>
                  </div>
                  <Input
                    id={`entrada-incluye-${i}`}
                    aria-label={`Qué incluye la entrada ${i + 1}`}
                    placeholder="Entrada para la pareja; un libro"
                    value={e.incluye}
                    onChange={(ev) => onChange({ ...form, entradas: form.entradas.map((x, j) => (j === i ? { ...x, incluye: ev.target.value } : x)) })}
                  />
                </div>
              ))}
              {errores['entrada:repetida'] && <p className="text-xs text-red-600">{errores['entrada:repetida']}</p>}
              {form.entradas.length < MAX_ENTRADAS && (
                <Button type="button" variant="secondary" onClick={() => onChange({ ...form, entradas: [...form.entradas, { nivel: '', precio: '', incluye: '' }] })}>
                  Agregar una entrada
                </Button>
              )}
              {form.entradas.length === 0 && (
                <p className={ayuda}>Sin entradas cargadas, el agente no dice ningún precio: dice que lo confirma y pasa el chat.</p>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardBody className="space-y-3 p-4">
              <div>
                <h2 className={titulo}>Videos de presentación</h2>
                <p className={ayuda}>
                  Se lo manda el agente una sola vez, en cuanto sabe para qué quiere la propiedad, antes de ofrecer el Zoom.
                  MP4 o MOV, hasta 25 MB. Vacío = no manda ninguno.
                </p>
              </div>
              {VIDEOS_DE_PRESENTACION.map(({ clave, etiqueta, detalle }) => {
                const v = form.videos[clave];
                const subiendo = subiendoVideo === clave;
                return (
                  <div key={clave} className="space-y-2 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                    <div className="flex flex-wrap items-center justify-between gap-x-3">
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {etiqueta}
                        {detalle && <span className="font-normal text-slate-500 dark:text-slate-400"> ({detalle})</span>}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-3 text-sm text-slate-600 dark:text-slate-300">
                        {v.key ? (
                          <a href={`/media/${v.key}`} target="_blank" rel="noreferrer" className="inline-flex min-h-[44px] items-center gap-1 underline">
                            <Film className="h-4 w-4" /> Ver el video
                          </a>
                        ) : (
                          <span className="text-xs text-slate-500">Sin video</span>
                        )}
                        <label
                          className={`inline-flex min-h-[44px] items-center gap-1 underline ${onSubirVideo && !subiendoVideo ? 'cursor-pointer' : 'opacity-60'}`}
                        >
                          <Upload className="h-4 w-4" /> {subiendo ? 'Subiendo…' : v.key ? 'Cambiar el video' : 'Subir video'}
                          <input
                            type="file"
                            accept="video/mp4,video/quicktime"
                            className="hidden"
                            disabled={!onSubirVideo || !!subiendoVideo}
                            onChange={(e) => {
                              const a = e.target.files?.[0];
                              e.target.value = '';
                              if (a) onSubirVideo?.(clave, a);
                            }}
                          />
                        </label>
                        {v.key && (
                          <button type="button" className="min-h-[44px] underline" onClick={() => setVideo(clave, { key: '' })}>
                            Quitar
                          </button>
                        )}
                      </div>
                    </div>
                    <Input
                      label="Lo que va con el video (puede llevar {{nombre}})"
                      maxLength={300}
                      value={v.texto}
                      onChange={(e) => setVideo(clave, { texto: e.target.value })}
                    />
                  </div>
                );
              })}
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}
